import { Grid, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useMemo, useContext } from 'react';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext } from 'src/store/ContextStore';
import { downloadFile } from 'src/util/util';
import HistoryIcon from '@mui/icons-material/History';
import CanRiskModal from './export-menu/CanRiskModal';
import RiskRecordsList from './RiskRecordsList';
import GailScoreModal from './GailScoreModal';

const ButtonGroup = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 15
});

function RiskFactorsMenu({ data, modificationHistoryHandle }) {
  const { setSnack } = useContext(SnackContext);
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);
  const [gailModalOpen, setGailModalOpen] = useState(false);
  const [canRiskModalOpen, setCanRiskModalOpen] = useState(false);

  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const user_id = useMemo(() => user?.user_id || user?._id, [user]);
  const familyId = user?.familyId || user_id;
  const gender = useMemo(() => data?.gender, [data]);
  // Canonical member key — scopes the score cards to the selected member.
  const memberId = useMemo(
    () => data?.proBandId ?? data?.familyMemberId,
    [data?.proBandId, data?.familyMemberId]
  );

  const handleCanRiskDownload = (allOptionsArray, targetId) => {
    if (!user_id) {
      return setSnack({
        status: true,
        msg: 'Please save the pedigree first',
        severity: 'error'
      });
    }

    axios
      .post(
        APICONSTANTS.getCanRiskData.replace(':user_id', user_id),
        { members: allOptionsArray, targetId },
        {
          responseType: 'blob'
        }
      )
      .then((res) => downloadFile(res.data, familyId, 'txt'))
      .catch(() =>
        setSnack({
          status: true,
          msg: 'Error exporting CanRisk pedigree',
          severity: 'error'
        })
      );
  };

  return (
    <div style={{ maxWidth: 390 }}>
      <Grid container spacing={2}>
        {gender === 'female' && (
          <ButtonGroup size={12}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setGailModalOpen(true)}
              sx={{ width: '100%' }}
            >
              Calculate Gail Score
            </Button>
          </ButtonGroup>
        )}

        <ButtonGroup size={12}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCanRiskModalOpen(true)}
            sx={{ width: '100%' }}
          >
            Calculate CanRisk Score
          </Button>
        </ButtonGroup>

        {gender === 'female' && (
          <Grid size={12}>
            <RiskRecordsList
              userId={user_id}
              refreshKey={recordsRefreshKey}
              recordType="gail"
              memberIds={[memberId]}
              scoredOnly
            />
          </Grid>
        )}
        <Grid size={12}>
          <RiskRecordsList
            userId={user_id}
            refreshKey={recordsRefreshKey}
            recordType="canrisk"
            memberIds={[memberId]}
            scoredOnly
          />
        </Grid>
        {gender === 'female' && (
          <Grid
            size={12}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <IconButton
              onClick={() =>
                modificationHistoryHandle(CONSTANTS.auditLogType['riskFactors'])
              }
            >
              <HistoryIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {/* Calculate Gail Score - fetches saved inputs on open (no form) */}
      <GailScoreModal
        open={gailModalOpen}
        onClose={() => setGailModalOpen(false)}
        data={data}
        familyId={familyId}
        onCalculated={() => setRecordsRefreshKey((k) => k + 1)}
      />
      <CanRiskModal
        data={data}
        open={canRiskModalOpen}
        onClose={(hadResult) => {
          setCanRiskModalOpen(false);
          if (hadResult) setRecordsRefreshKey((k) => k + 1);
        }}
        onDownload={handleCanRiskDownload}
        userId={user_id}
      />
    </div>
  );
}

export default RiskFactorsMenu;
